import React, { useState } from "react";
import { Check, Loader2, Clock, CreditCard, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { PageHeader } from "@/components/shell/TopBar";
import { useLaunch } from "@/context/LaunchContext";
import { startTrial, stripeCheckout, postLaunchEvent, PlanCode } from "@/api/launch";
import { ManageSubscriptionButton, hasManageableSubscription } from "@/components/billing/ManageSubscriptionButton";
import { fmtDuration } from "@/lib/format";
import { useI18n } from "@/i18n";
import { toast } from "sonner";

type Period = "monthly" | "yearly";

// Only navigate to a valid absolute https:// URL returned by the backend (open-redirect guard).
function isHttpsUrl(u: string | null | undefined): boolean {
  if (!u) return false;
  try { return new URL(u).protocol === "https:"; } catch { return false; }
}

const PLANS: { key: PlanCode; mo: string; yr: string; paidKey?: "pro" | "elite" | "premium"; featsTr: string[]; featsEn: string[]; highlight?: boolean }[] = [
  { key: "FREE", mo: "£0", yr: "£0",
    featsTr: ["Genel Bakış", "Canlı & maç öncesi değer önizlemesi", "Sınırlı erişim"],
    featsEn: ["Overview", "Live & prematch value preview", "Limited access"] },
  { key: "PRO", mo: "£9.99", yr: "£79.99", paidKey: "pro", highlight: true,
    featsTr: ["Gerçek zamanlı canlı sinyaller", "Maç öncesi sinyaller", "Sinyal geçmişi"],
    featsEn: ["Real-time live signals", "Prematch signals", "Signal history"] },
  { key: "ELITE", mo: "£19.99", yr: "£159.99", paidKey: "elite",
    featsTr: ["Tüm PRO özellikleri", "Gelişmiş ATLAS analizleri", "Daha yüksek kota", "Öncelikli destek"],
    featsEn: ["Everything in PRO", "Advanced ATLAS analysis", "Higher quota", "Priority support"] },
  { key: "PREMIUM", mo: "£29.99", yr: "£239.99", paidKey: "premium",
    featsTr: ["Tüm ELITE özellikleri", "ATLAS Kupon Merkezi", "AI kuponları & kupon geçmişi", "Gelişmiş market intelligence"],
    featsEn: ["Everything in ELITE", "ATLAS Coupon Center", "AI coupons & coupon history", "Advanced market intelligence"] },
];

export default function Membership() {
  const { lang } = useI18n();
  const { me, loading, error, plan, basePlan, isTrial, trialConsumed, secondsRemaining, refetch, planLabel, trialPlan } = useLaunch();
  const [period, setPeriod] = useState<Period>("monthly");
  const [busy, setBusy] = useState<string | null>(null);

  const paidSubscription = !!me?.paid_subscription;
  const canTrial = !loading && basePlan === "FREE" && !trialConsumed && !isTrial && !paidSubscription;

  async function onTrial() {
    setBusy("trial");
    try { await startTrial(); refetch(); toast.success(lang === "tr" ? "3 günlük ELITE denemen başladı" : "Your 3-day ELITE trial has started"); }
    catch { toast.error(lang === "tr" ? "Deneme başlatılamadı. Lütfen tekrar deneyin." : "Could not start trial. Please try again."); }
    finally { setBusy(null); }
  }
  async function onCheckout(p: "pro" | "elite" | "premium") {
    setBusy(p);
    postLaunchEvent("checkout_started", { plan: p, period });
    try {
      const url = await stripeCheckout(p, period);
      if (isHttpsUrl(url)) { window.location.href = url; return; }
      toast.error(lang === "tr" ? "Ödeme başlatılamadı. Lütfen tekrar deneyin." : "Could not start checkout. Please try again.");
    } catch { toast.error(lang === "tr" ? "Ödeme başlatılamadı. Lütfen tekrar deneyin." : "Could not start checkout. Please try again."); }
    finally { setBusy(null); }
  }

  return (
    <div data-testid="membership-page">
      <PageHeader title={lang === "tr" ? "Üyelik" : "Membership"}
        subtitle={lang === "tr" ? "Planını seç, zekâ erişimini yükselt." : "Choose your plan and upgrade your intelligence access."} />

      {/* current status / trial */}
      <div className="panel relative mb-6 overflow-hidden p-6" data-testid="current-plan">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <p className="eyebrow">{lang === "tr" ? "Mevcut Plan" : "Current Plan"}</p>
            <div className="mt-2 flex items-center gap-3">
              <h2 className="text-2xl font-bold text-atlas-300" data-testid="membership-current-label">{planLabel(lang) ?? (loading ? "…" : "FREE")}</h2>
              {isTrial && secondsRemaining != null && (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-gold/25 bg-gold/10 px-2.5 py-1 text-[11px] font-semibold text-gold-300" data-testid="membership-trial-countdown">
                  <Clock className="h-3 w-3" /> {fmtDuration(secondsRemaining, lang)} {lang === "tr" ? "kaldı" : "left"}
                </span>
              )}
            </div>
            {error && <p className="mt-2 text-sm text-muted-foreground">{lang === "tr" ? "Üyelik bilgisi şu anda alınamadı. Giriş yaptıysan birazdan tekrar dene." : "Membership info is unavailable right now. If you're signed in, try again shortly."}</p>}
          </div>
          {hasManageableSubscription(me) && (
            <div className="w-full sm:w-auto sm:min-w-[220px]" data-testid="membership-manage-subscription">
              <ManageSubscriptionButton lang={lang} />
            </div>
          )}
        </div>

        {canTrial && (
          <div className="mt-5 rounded-2xl border border-atlas/25 bg-atlas/[0.06] p-5" data-testid="trial-offer">
            <h3 className="text-lg font-bold text-foreground">{lang === "tr" ? "SDIP AI'ı 3 gün ücretsiz dene" : "Try SDIP AI free for 3 days"}</h3>
            <ul className="mt-3 space-y-1.5 text-sm text-foreground/90">
              {(lang === "tr" ? ["72 saat tam ELITE erişim", "Kart gerekmez", "Deneme sonunda ödeme yapmazsan hesabın otomatik FREE plana döner."]
                : ["72 hours of full ELITE access", "No card required", "If you don't pay when the trial ends, your account automatically returns to FREE."]).map((t) => (
                <li key={t} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-atlas-400" /> {t}</li>
              ))}
            </ul>
            <button onClick={onTrial} disabled={busy === "trial"} data-testid="start-trial-btn"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-atlas-500 to-atlas-600 px-5 py-2.5 text-sm font-bold text-atlas-ink transition hover:shadow-glow">
              {busy === "trial" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {lang === "tr" ? "3 Gün Ücretsiz Başla" : "Start 3-Day Free Trial"}
            </button>
          </div>
        )}
        {!loading && basePlan === "FREE" && trialConsumed && !isTrial && !paidSubscription && (
          <div className="mt-5 rounded-2xl border border-white/8 bg-white/[0.02] p-5 text-sm text-muted-foreground" data-testid="trial-ended">
            {lang === "tr" ? "Deneme süren sona erdi. Hesabın ve geçmişin korunuyor. Devam etmek için planını seç." : "Your trial has ended. Your account and history are preserved. Choose a plan to continue."}
          </div>
        )}
      </div>

      {/* billing period toggle */}
      <div className="mb-5 flex items-center justify-center gap-2">
        {(["monthly", "yearly"] as Period[]).map((p) => (
          <button key={p} onClick={() => setPeriod(p)} data-testid={`period-${p}`}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition ${period === p ? "bg-atlas-500 text-atlas-ink" : "border border-white/12 text-muted-foreground hover:text-foreground"}`}>
            {p === "monthly" ? (lang === "tr" ? "Aylık" : "Monthly") : (lang === "tr" ? "Yıllık" : "Yearly")}
          </button>
        ))}
      </div>

      {/* plans */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {PLANS.map((pl) => {
          const isCurrent = pl.key === plan && !isTrial;
          const price = period === "monthly" ? pl.mo : pl.yr;
          return (
            <motion.div key={pl.key} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
              className={`panel relative flex flex-col p-6 ${pl.highlight ? "ring-1 ring-atlas/40" : ""}`} data-testid={`membership-tier-${pl.key.toLowerCase()}`}>
              {pl.highlight && <span className="absolute -top-3 left-6 rounded-full bg-atlas/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-atlas-300">{lang === "tr" ? "Popüler" : "Popular"}</span>}
              {isCurrent && <span className="absolute -top-3 right-6 rounded-full border border-atlas/30 bg-atlas/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-atlas-300">{lang === "tr" ? "Aktif" : "Active"}</span>}
              <h3 className="text-lg font-bold text-foreground">{pl.key}</h3>
              <div className="mt-3 flex items-end gap-1">
                <span className="num text-3xl font-extrabold text-foreground">{price}</span>
                {pl.key !== "FREE" && <span className="mb-1 text-xs text-muted-foreground">/{period === "monthly" ? (lang === "tr" ? "ay" : "mo") : (lang === "tr" ? "yıl" : "yr")}</span>}
              </div>
              <ul className="mt-5 flex-1 space-y-2.5">
                {(lang === "tr" ? pl.featsTr : pl.featsEn).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground"><Check className="mt-0.5 h-4 w-4 shrink-0 text-atlas-400" /> {f}</li>
                ))}
              </ul>
              {pl.paidKey ? (
                <button onClick={() => onCheckout(pl.paidKey!)} disabled={busy === pl.paidKey || isCurrent} data-testid={`membership-cta-${pl.key.toLowerCase()}`}
                  className={`mt-6 inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-center text-sm font-bold transition disabled:opacity-60 ${pl.highlight ? "bg-gradient-to-r from-atlas-500 to-atlas-600 text-atlas-ink hover:shadow-glow" : "border border-atlas/30 text-atlas-300 hover:bg-atlas/10"}`}>
                  {busy === pl.paidKey ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                  {isCurrent ? (lang === "tr" ? "Mevcut plan" : "Current plan") : (lang === "tr" ? "Satın Al" : "Subscribe")}
                </button>
              ) : (
                <span data-testid="membership-cta-free" className="mt-6 cursor-default rounded-full border border-white/10 px-4 py-2.5 text-center text-sm font-bold text-muted-foreground">
                  {plan === "FREE" ? (lang === "tr" ? "Mevcut plan" : "Current plan") : (lang === "tr" ? "Temel" : "Base")}
                </span>
              )}
            </motion.div>
          );
        })}
      </div>
      <p className="mt-5 text-center text-xs text-muted-foreground">
        {lang === "tr" ? "Fiyatlar GBP cinsindendir; gerekli durumlarda ilgili vergiler eklenebilir. Ücretli abonelikler iptal edilene kadar otomatik yenilenir." : "Prices are in GBP; applicable taxes may be added where required. Paid subscriptions renew automatically until cancelled."}
      </p>
    </div>
  );
}
